import EFArrowDown from '@/components/UI/svg/Icons/EFArrowDown';
import EFAddress from '@/components/UI/svg/WTB/EFAddress';
import EFClock from '@/components/UI/svg/WTB/EFClock';
import EFEmail from '@/components/UI/svg/WTB/EFEmail';
import EFGlobal from '@/components/UI/svg/WTB/EFGlobal';
import EFLocation from '@/components/UI/svg/WTB/EFLocation';
import EFNoData from '@/components/UI/svg/WTB/EFNoData';
import EFPageArrow from '@/components/UI/svg/WTB/EFPageArrow';
import EFPhone from '@/components/UI/svg/WTB/EFPhone';
import EFZip from '@/components/UI/svg/WTB/EFZip';
import { getCityList, getCountryList, getStateList, getStoreList, getTypeCodeList, getTypeList } from '@/service';
import { useGlobal } from '@/utils/useGlobal';
import { Input, Select } from 'antd';

interface RetailStorePanelH5Props {
    // 控制当前面板是否处于激活态（用于切换 tab 显隐）
    visible: boolean;
    // 固定门店类型 code（如 '1' 零售商、'2' 安装商）。传入后隐藏类型下拉，所有请求按该类型对应的名称过滤
    typeCode?: string;
}

export default function RetailStorePanelH5({ visible, typeCode }: RetailStorePanelH5Props) {
    const { useLang, useRouter, useState, useEffect, gtm } = useGlobal();
    const { t } = useLang('wheretobuy');
    const { locale } = useRouter();
    const PER_PAGE_MAX = 3;

    const [storeLists, setStoreLists] = useState([]);
    const [curPageStores, setCurPageStores] = useState([]);
    const [curPage, setCurPage] = useState(1);

    const [countryData, setCountryData] = useState([]);
    const [provinceData, setProvinceData] = useState([]);
    const [cityData, setCityData] = useState([]);
    const [typeData, setTypeData] = useState([]);

    const [countryOpen, setCountryOpen] = useState(false);
    const [provinceOpen, setProvinceOpen] = useState(false);
    const [cityOpen, setCityOpen] = useState(false);
    const [typeOpen, setTypeOpen] = useState(false);

    const [searchCountry, setSearchCountry] = useState(null);
    const [searchProvince, setSearchProvince] = useState(null);
    const [searchCity, setSearchCity] = useState(null); // 仅中国站
    const [searchType, setSearchType] = useState(null);
    const [searchText, setSearchText] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const SEARCH_VAR_SITES: string[] = ['us', 'ca', 'de', 'au', 'ph', 'jp', 'it', 'es', 'fr', 'eu', 'uk', 'br', 'mx'];
    const SEARCH_VAR_SITES_STORE: string[] = ['sa'];
    const [expanded, setExpanded] = useState(false);
    const [searchVar, setSearchVar] = useState(SEARCH_VAR_SITES.includes(locale) ? 'zip' : SEARCH_VAR_SITES_STORE.includes(locale) ? 'text' : ''); // text => 地址或店名, zip => 邮编 两种
    const [countryStores, setCountryStores] = useState([]);

    const LOCAL_MAP: any = {
        de: 'Germany',
        it: 'Italy',
        es: 'Spain',
        fr: 'France',
        us: 'United States',
        au: 'Australia',
        ph: 'Philippines',
        jp: 'Japan',
        lu: 'Luxembourg',
        cn: 'China',
        ca: 'Canada',
        eu: 'Germany',
        uk: 'United Kingdom',
        sa: 'Saudi Arabia',
        br: 'Brasil',
        mx: 'Mexico',
    };

    // 根据 typeCode 解析出对应国家下的门店类型名称（请求门店时按名称过滤）
    const resolveFixedTypeName = async (country: string) => {
        if (!typeCode) {
            return undefined;
        }
        const res: any = await getTypeCodeList({ country }, locale);
        if (res?.code === '0') {
            const matched = res?.data?.find((item: any) => String(item?.typeCode) === String(typeCode));
            return matched?.name;
        }
        return undefined;
    };

    const onSearch = async (v: string) => {
        const params: any = {};
        searchVar === 'zip' ? (params.zipCode = v) : (params.fuzzyStr = v);
        if (searchCountry) {
            params.country = searchCountry;
        }
        if (searchProvince) {
            params.state = searchProvince;
        }
        if (searchCity && locale === 'cn') {
            params.city = searchCity;
        }
        if (searchType) {
            params.type = searchType;
        }
        const res = await getStoreList(params, locale);
        if (res.code === '0') {
            setStoreLists(res.data);
            setCurPageStores(res.data.slice(0, PER_PAGE_MAX));
            setCurPage(1);
        }
    };

    const onSearchChange = (e: any) => {
        setSearchText(e.target.value);
    };

    const onChangeCountry = async (_k: any, v: any) => {
        if (v && v?.value) {
            // 1. 请求省/州数据
            const res = await getStateList({ country: v.value }, locale);
            if (res.code === '0') {
                setProvinceData(res.data);
                setSearchCountry(v.value);
                setSearchProvince(null);
                setSearchType(null);
            }
            // 2. 更新地图门店数据（选中国家下的）
            const params: any = { country: v.value };
            if (typeCode) {
                const typeName = await resolveFixedTypeName(v.value);
                setSearchType(typeName ?? null);
                if (!typeName) {
                    // 新国家没有对应的固定门店类型：不请求门店，直接清空数据
                    setStoreLists([]);
                    setCountryStores([]);
                    setCurPageStores([]);
                    setCurPage(1);
                    return;
                }
                params.type = typeName;
            } else if (searchType) {
                params.type = searchType;
            }
            if (searchText) {
                searchVar === 'zip' ? (params.zipCode = searchText) : (params.fuzzyStr = searchText);
            }
            const resStores = await getStoreList(params, locale);
            if (resStores.code === '0') {
                setStoreLists(resStores.data);
                setCountryStores(resStores.data);
                setCurPageStores(resStores.data.slice(0, PER_PAGE_MAX));
                setCurPage(1);
            }
            if (locale !== 'cn' && !typeCode) {
                // 3. 更新类型
                const resType = await getTypeList({ country: v.value }, locale);
                if (resType.code === '0') {
                    setTypeData(resType.data);
                }
            }
        }
    };

    const onChangeProvince = async (_k: any, v: any) => {
        if (v && v?.value) {
            setSearchProvince(v.value);
            // 1. 请求城市数据
            if (locale === 'cn') {
                const res = await getCityList({ country: searchCountry, state: v.value }, locale);
                if (res.code === '0') {
                    setCityData(res.data);
                    setSearchCity(null);
                }
            }
            // 2. 更新地图门店数据（选中省/州下的）
            const params: any = { country: searchCountry, state: v.value };
            if (searchType) {
                params.type = searchType;
            }
            if (searchText) {
                searchVar === 'zip' ? (params.zipCode = searchText) : (params.fuzzyStr = searchText);
            }
            const resStores = await getStoreList(params, locale);
            if (resStores.code === '0') {
                setStoreLists(resStores.data);
                setCurPageStores(resStores.data.slice(0, PER_PAGE_MAX));
                setCurPage(1);
            }
        }
    };

    const onChangeCity = async (_k: any, v: any) => {
        if (v && v?.value) {
            setSearchCity(v.value);
            // 2. 更新地图门店数据（选中城市下的）
            const params: any = { country: 'CN', state: searchProvince, city: v.value };
            if (searchType) {
                params.type = searchType;
            }
            if (searchText) {
                searchVar === 'zip' ? (params.zipCode = searchText) : (params.fuzzyStr = searchText);
            }
            const resStores = await getStoreList(params, locale);
            if (resStores.code === '0') {
                setStoreLists(resStores.data);
                setCurPageStores(resStores.data.slice(0, PER_PAGE_MAX));
                setCurPage(1);
            }
        }
    };

    const onChangeType = async (_k: any, v: any) => {
        if (v && v?.value) {
            setSearchType(v.value);
            // 更新地图门店数据（选中类型下的）
            const params: any = { type: v.value };
            if (searchText) {
                searchVar === 'zip' ? (params.zipCode = searchText) : (params.fuzzyStr = searchText);
            }
            if (searchCountry) {
                params.country = searchCountry;
            }
            if (searchProvince) {
                params.state = searchProvince;
            }
            if (searchCity && locale === 'cn') {
                params.city = searchCity;
            }
            const resStores = await getStoreList(params, locale);
            if (resStores.code === '0') {
                setStoreLists(resStores.data);
                setCurPageStores(resStores.data.slice(0, PER_PAGE_MAX));
                setCurPage(1);
            }
        }
    };

    const onPrevPage = () => {
        if (curPage > 1) {
            setCurPage(curPage - 1);
            setCurPageStores(storeLists.slice(PER_PAGE_MAX * (curPage - 2), PER_PAGE_MAX * (curPage - 1)));
        }
    };

    const onNextPage = () => {
        if (curPage < Math.ceil(storeLists.length / PER_PAGE_MAX)) {
            setCurPage(curPage + 1);
            setCurPageStores(storeLists.slice(PER_PAGE_MAX * curPage, PER_PAGE_MAX * (curPage + 1)));
        }
    };

    const onChangeSearch = (value: string) => {
        setSearchVar(value);
        if (locale === 'de') {
            gtm.push({
                event: 'uaEvent',
                event_name: 'tab_click',
                list_name: t('title'),
                button_name: value === 'zip' ? t('search_item_zip') : t('search_item_address'),
            });
        } else {
            gtm.push({
                event: 'uaEvent',
                event_name: 'option_click',
                button_name: value === 'zip' ? t('search_item_zip') : t('search_item_address'),
            });
        }
    };

    const selectBefore: any = (
        <Select
            defaultValue={searchVar}
            open={expanded}
            onDropdownVisibleChange={setExpanded}
            onChange={(value) => onChangeSearch(value)}
            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
            suffixIcon={
                ['zip', 'text'].includes(searchVar) ? (
                    <>
                        {searchVar === 'zip' ? (
                            <EFZip width=".16rem" height=".16rem" className="svg-icon" />
                        ) : (
                            <EFAddress width=".16rem" height=".16rem" className="svg-icon" />
                        )}
                        <svg
                            className={`svg-arrow ${expanded ? 'open' : 'close'}`}
                            xmlns="http://www.w3.org/2000/svg"
                            width=".12rem"
                            height=".12rem"
                            viewBox="0 0 12 12"
                            fill="none"
                        >
                            <path
                                d="M2.89534 7.69762L5.99995 4.62117L9.10456 7.69762C9.24026 7.83209 9.40735 7.9 9.59995 7.9C9.79255 7.9 9.95964 7.83209 10.0953 7.69762C10.2311 7.56311 10.3 7.39711 10.3 7.20544C10.3 7.01377 10.2311 6.84777 10.0953 6.71326L6.49534 3.14592L6.42627 3.21562L6.49534 3.14592C6.3437 2.99566 6.18141 2.9054 6.0092 2.90023C5.83547 2.89502 5.67503 2.977 5.52956 3.12115L1.90456 6.71326C1.76883 6.84777 1.69995 7.01377 1.69995 7.20544C1.69995 7.39711 1.76883 7.56311 1.90456 7.69762C2.04027 7.83209 2.20735 7.9 2.39995 7.9C2.59255 7.9 2.75964 7.83209 2.89534 7.69762Z"
                                fill="#919191"
                                stroke="#919191"
                                strokeWidth="0.2"
                            />
                        </svg>
                    </>
                ) : null
            }
        >
            <Select.Option value="zip">
                <>
                    <EFZip width=".16rem" height=".16rem" />
                    <span className="option-text">{t('search_item_zip')}</span>
                </>
            </Select.Option>
            <Select.Option value="text">
                <>
                    <EFAddress width=".16rem" height=".16rem" />
                    <span className="option-text">{t('search_item_address')}</span>
                </>
            </Select.Option>
        </Select>
    );

    useEffect(() => {
        if (locale !== 'cn') {
            getCountryList(locale).then((res: any) => {
                if (res.code === '0') {
                    setCountryData(res.data);
                }
            });
        }
        const defaultCountry = LOCAL_MAP[locale] || locale?.toUpperCase();
        // 默认展示当前站点国家的省/州
        getStateList({ country: defaultCountry }, locale).then((res: any) => {
            if (res.code === '0') {
                setSearchCountry(defaultCountry);
                setProvinceData(res.data);
            }
        });
        // 请求门店（typeCode 模式下按固定类型名称过滤）
        const loadStores = (typeName?: string) => {
            const params: any = { country: defaultCountry };
            if (typeName) {
                params.type = typeName;
            }
            getStoreList(params, locale).then((res: any) => {
                if (res.code === '0') {
                    setStoreLists(res.data);
                    setCountryStores(res.data);
                    setCurPageStores(res.data.slice(0, PER_PAGE_MAX));
                    setCurPage(1);
                }
            });
        };
        if (typeCode) {
            resolveFixedTypeName(defaultCountry).then((typeName) => {
                if (typeName) {
                    setSearchType(typeName);
                    loadStores(typeName);
                } else {
                    // 该国家没有对应的固定门店类型：不请求门店，直接清空数据
                    setSearchType(null);
                    setStoreLists([]);
                    setCountryStores([]);
                    setCurPageStores([]);
                    setCurPage(1);
                }
            });
        } else {
            loadStores();
            // 请求类型
            getTypeList({ country: defaultCountry }, locale).then((res: any) => {
                if (res.code === '0') {
                    setTypeData(res.data);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const hasZipStore = countryStores?.some((list: any) => Boolean(list?.zipCode));
        if (hasZipStore && SEARCH_VAR_SITES.includes(locale)) {
            !searchVar && setSearchVar('zip');
        } else if (SEARCH_VAR_SITES_STORE.includes(locale)) {
            !searchVar && setSearchVar('text');
        } else {
            setSearchVar('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SEARCH_VAR_SITES, SEARCH_VAR_SITES_STORE, locale, countryStores]);

    return (
        <div className={`retailStoreBox ${visible ? 'active' : ''}`}>
            <div className="controlBox">
                {locale !== 'cn' && (
                    <div className="countryBox controlItem">
                        <Select
                            placeholder={t('placeholder_country')}
                            notFoundContent={t('select_nodata')}
                            onChange={onChangeCountry}
                            onDropdownVisibleChange={(open: boolean) => {
                                if (open) {
                                    document.body.style.overflow = 'hidden'; // 阻止外层页面滚动
                                } else {
                                    document.body.style.overflow = ''; // 恢复外层页面滚动
                                }
                                if (open && !countryOpen) {
                                    return setCountryOpen(true);
                                }
                                if (!open && countryOpen) {
                                    return setCountryOpen(false);
                                }
                            }}
                            allowClear={true}
                            onClear={async () => {
                                if (locale !== 'cn') {
                                    setSearchCountry(null);
                                }
                                if (locale === 'cn') {
                                    setSearchCity(null);
                                }
                                setSearchProvince(null);
                                setProvinceData([]);
                                if (!typeCode) {
                                    setSearchType(null);
                                    setTypeData([]);
                                }
                                // 重新请求数据
                                const params: any = { country: locale?.toUpperCase() };
                                if (typeCode) {
                                    const typeName = await resolveFixedTypeName(locale?.toUpperCase());
                                    setSearchType(typeName ?? null);
                                    if (!typeName) {
                                        // 该国家没有对应的固定门店类型：不请求门店，直接清空数据
                                        setStoreLists([]);
                                        setCurPageStores([]);
                                        setCurPage(1);
                                        return;
                                    }
                                    params.type = typeName;
                                }
                                if (searchText) {
                                    searchVar === 'zip' ? (params.zipCode = searchText) : (params.fuzzyStr = searchText);
                                }
                                const res = await getStoreList(params, locale);
                                if (res?.code === '0') {
                                    setStoreLists(res.data);
                                    setCurPageStores(res.data.slice(0, PER_PAGE_MAX));
                                    setCurPage(1);
                                }
                            }}
                            value={searchCountry}
                            options={countryData.map((country: any) => ({ label: country, value: country }))}
                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                            suffixIcon={
                                <span
                                    style={{
                                        display: 'block',
                                        transition: 'all 0.3s ease-in-out',
                                        transform: countryOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                >
                                    <EFArrowDown style={{ fontSize: '0.15rem' }} />
                                </span>
                            }
                        />
                    </div>
                )}
                {provinceData.length > 0 && (
                    <div className="provinceBox controlItem">
                        <Select
                            placeholder={t('placeholder_province')}
                            notFoundContent={t('select_nodata')}
                            onChange={onChangeProvince}
                            onDropdownVisibleChange={(open: boolean) => {
                                if (open) {
                                    document.body.style.overflow = 'hidden'; // 阻止外层页面滚动
                                } else {
                                    document.body.style.overflow = ''; // 恢复外层页面滚动
                                }
                                if (open && !provinceOpen) {
                                    return setProvinceOpen(true);
                                }
                                if (!open && provinceOpen) {
                                    return setProvinceOpen(false);
                                }
                            }}
                            allowClear={true}
                            onClear={async () => {
                                setSearchProvince(null);
                                if (locale === 'cn') {
                                    setSearchCity(null);
                                }
                                // 重新请求数据
                                const params: any = { country: searchCountry };
                                if (searchText) {
                                    searchVar === 'zip' ? (params.zipCode = searchText) : (params.fuzzyStr = searchText);
                                }
                                if (searchType) {
                                    params.type = searchType;
                                }
                                const res = await getStoreList(params, locale);
                                if (res?.code === '0') {
                                    setStoreLists(res.data);
                                    setCurPageStores(res.data.slice(0, PER_PAGE_MAX));
                                    setCurPage(1);
                                }
                            }}
                            value={searchProvince}
                            options={provinceData.map((province: any) => ({ label: province, value: province }))}
                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                            suffixIcon={
                                <span
                                    style={{
                                        display: 'block',
                                        transition: 'all 0.3s ease-in-out',
                                        transform: provinceOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                >
                                    <EFArrowDown style={{ fontSize: '0.15rem' }} />
                                </span>
                            }
                        />
                    </div>
                )}
                {locale === 'cn' && (
                    <div className="cityBox controlItem">
                        <Select
                            placeholder={t('placeholder_city')}
                            notFoundContent={t('select_nodata')}
                            onChange={onChangeCity}
                            onDropdownVisibleChange={(open: boolean) => {
                                if (open) {
                                    document.body.style.overflow = 'hidden'; // 阻止外层页面滚动
                                } else {
                                    document.body.style.overflow = ''; // 恢复外层页面滚动
                                }
                                if (open && !cityOpen) {
                                    return setCityOpen(true);
                                }
                                if (!open && cityOpen) {
                                    return setCityOpen(false);
                                }
                            }}
                            allowClear={true}
                            onClear={async () => {
                                setSearchCity(null);
                                // 重新请求数据
                                const params: any = { country: searchCountry, state: searchProvince };
                                if (searchText) {
                                    searchVar === 'zip' ? (params.zipCode = searchText) : (params.fuzzyStr = searchText);
                                }
                                if (searchType) {
                                    params.type = searchType;
                                }
                                const res = await getStoreList(params, locale);
                                if (res?.code === '0') {
                                    setStoreLists(res.data);
                                    setCurPageStores(res.data.slice(0, PER_PAGE_MAX));
                                    setCurPage(1);
                                }
                            }}
                            value={searchCity}
                            options={cityData.map((city: any) => ({ label: city, value: city }))}
                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                            suffixIcon={
                                <span
                                    style={{
                                        display: 'block',
                                        transition: 'all 0.3s ease-in-out',
                                        transform: cityOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                >
                                    <EFArrowDown style={{ fontSize: '0.15rem' }} />
                                </span>
                            }
                        />
                    </div>
                )}
                {!typeCode && (
                    <div className="storeBox controlItem">
                        <Select
                            placeholder={t('placeholder_type')}
                            notFoundContent={t('select_nodata')}
                            onChange={onChangeType}
                            onDropdownVisibleChange={(open: boolean) => {
                                if (open) {
                                    document.body.style.overflow = 'hidden'; // 阻止外层页面滚动
                                } else {
                                    document.body.style.overflow = ''; // 恢复外层页面滚动
                                }
                                if (open && !typeOpen) {
                                    return setTypeOpen(true);
                                }
                                if (!open && typeOpen) {
                                    return setTypeOpen(false);
                                }
                            }}
                            allowClear={true}
                            onClear={async () => {
                                setSearchType(null);
                                // 重新请求数据
                                const params: any = { country: searchCountry };
                                if (searchProvince) {
                                    params.state = searchProvince;
                                }
                                if (searchCity) {
                                    params.city = searchCity;
                                }
                                const res = await getStoreList(params, locale);
                                if (res?.code === '0') {
                                    setStoreLists(res.data);
                                    setCurPageStores(res.data.slice(0, PER_PAGE_MAX));
                                    setCurPage(1);
                                }
                            }}
                            value={searchType}
                            options={typeData.map((type: any) => ({ label: type, value: type }))}
                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                            suffixIcon={
                                <span
                                    style={{
                                        display: 'block',
                                        transition: 'all 0.3s ease-in-out',
                                        transform: typeOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                >
                                    <EFArrowDown style={{ fontSize: '0.15rem' }} />
                                </span>
                            }
                        />
                    </div>
                )}
                <div className="searchBox controlItem">
                    <Input.Search
                        className={`${(SEARCH_VAR_SITES.includes(locale) || SEARCH_VAR_SITES_STORE.includes(locale)) && searchVar ? 'search-var' : ''}`}
                        addonBefore={(SEARCH_VAR_SITES.includes(locale) || SEARCH_VAR_SITES_STORE.includes(locale)) && searchVar ? selectBefore : null}
                        placeholder={searchVar === 'zip' ? t('placeholder_search_zip') : t('placeholder_search_address')}
                        size="large"
                        onSearch={onSearch}
                        onChange={onSearchChange}
                    />
                </div>
            </div>
            <div className="resultBox">
                {curPageStores?.map((storeList: any, k: number) => (
                    <li className="store_item" key={`${storeList?.latitude}_${storeList.longitude}_record_${k}`}>
                        {storeList?.name && <div className="store_name">{storeList?.name}</div>}
                        {storeList?.address && !storeList?.virtualFlag && (
                            <div className="store_address common">
                                <div className="icon">
                                    <EFLocation />
                                </div>
                                <div className="text">{storeList?.address}</div>
                            </div>
                        )}
                        {storeList?.phone && (
                            <div className="store_phone common">
                                <div className="icon">
                                    <EFPhone />
                                </div>
                                <a className="text" href={`tel:${storeList?.phone}`}>
                                    {storeList?.phone}
                                </a>
                            </div>
                        )}
                        {storeList?.email && (
                            <div className="store_email common">
                                <div className="icon">
                                    <EFEmail />
                                </div>
                                <a className="text" href={`mailto:${storeList?.email}`}>
                                    {storeList?.email}
                                </a>
                            </div>
                        )}
                        {storeList?.webLink && (
                            <div className="store_website common">
                                <div className="icon">
                                    <EFGlobal />
                                </div>
                                <a className="text" href={storeList?.webLink} target="_blank" rel="noreferrer">
                                    {storeList?.webLink}
                                </a>
                            </div>
                        )}
                        {storeList?.businessTime && (
                            <div className="store_openTime common">
                                <div className="icon">
                                    <EFClock />
                                </div>
                                <div className="text">{storeList?.businessTime}</div>
                            </div>
                        )}
                        {storeList?.label && (
                            <div className="store_tags">
                                {storeList?.label?.split(';')?.map((tag: any, m: number) => (
                                    <li className="store_tag_item" key={`${storeList.latitude}_${storeList.longitude}_record_${k}_tag_${m}`}>
                                        {tag}
                                    </li>
                                ))}
                            </div>
                        )}
                    </li>
                ))}
                {curPageStores?.length === 0 && (
                    <div className="noDataBox">
                        <EFNoData width="1.8rem" height="1.2rem" />
                        <div className="noData">{t('noData')}</div>
                    </div>
                )}
                {curPageStores?.length > 0 && (
                    <div className="pagenation">
                        <div className={`leftIcon ${curPage === 1 ? 'disable' : ''}`} onClick={onPrevPage}>
                            <EFPageArrow />
                        </div>
                        <div className="middleBox">
                            <span className="pages">
                                {curPage}/{Math.ceil(storeLists.length / PER_PAGE_MAX)}
                            </span>
                            <span className="total">
                                {storeLists.length === 1
                                    ? t.rich('total_single', {
                                          total: storeLists.length,
                                      })
                                    : t.rich('total', {
                                          total: storeLists.length,
                                      })}
                            </span>
                        </div>
                        <div className={`rightIcon ${curPage === Math.ceil(storeLists.length / PER_PAGE_MAX) ? 'disable' : ''}`} onClick={onNextPage}>
                            <EFPageArrow />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
